/**
 * StarRating Component
 * Interactive and display star rating
 * BP-08-04: Marketplace Frontend (Family)
 */

import React, { useState, useCallback } from 'react';
import { Star } from 'lucide-react';
import type { StarRatingProps } from '@/features/marketplace/types/marketplaceTypes';

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

export const StarRating: React.FC<StarRatingProps> = ({
  rating,
  maxRating = 5,
  size = 'md',
  interactive = false,
  onChange,
  showCount = false,
  count,
}) => {
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  
  const displayRating = hoverRating ?? rating;
  
  const handleClick = useCallback(
    (index: number) => {
      if (interactive && onChange) {
        onChange(index + 1);
      }
    },
    [interactive, onChange]
  );
  
  const handleMouseEnter = useCallback(
    (index: number) => {
      if (interactive) {
        setHoverRating(index + 1);
      }
    },
    [interactive]
  );
  
  const handleMouseLeave = useCallback(() => {
    if (interactive) {
      setHoverRating(null);
    }
  }, [interactive]);
  
  const renderStar = (index: number) => {
    const fillPercentage = Math.min(100, Math.max(0, (displayRating - index) * 100));
    const isFilled = fillPercentage >= 50;
    const isPartial = fillPercentage > 0 && fillPercentage < 100;
    
    return (
      <button
        key={index}
        type="button"
        className={`
          relative inline-flex
          ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
          ${sizeClasses[size]}
        `}
        onClick={() => handleClick(index)}
        onMouseEnter={() => handleMouseEnter(index)}
        onMouseLeave={handleMouseLeave}
        disabled={!interactive}
        aria-label={interactive ? `Rate ${index + 1} stars` : undefined}
      >
        {/* Background star (empty) */}
        <Star
          className={`
            absolute inset-0
            ${sizeClasses[size]}
            text-warm-gray/30
            stroke-current
          `}
          strokeWidth={1.5}
        />
        
        {/* Filled star */}
        {isPartial ? (
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ width: `${fillPercentage}%` }}
          >
            <Star
              className={`
                ${sizeClasses[size]}
                text-terracotta
                fill-current
                stroke-current
              `}
              strokeWidth={1.5}
            />
          </div>
        ) : isFilled ? (
          <Star
            className={`
              absolute inset-0
              ${sizeClasses[size]}
              text-terracotta
              fill-current
              stroke-current
            `}
            strokeWidth={1.5}
          />
        ) : null}
      </button>
    );
  };
  
  return (
    <div className="inline-flex items-center gap-1">
      <div
        className="inline-flex items-center"
        role={interactive ? 'radiogroup' : 'img'}
        aria-label={`Rating: ${rating} out of ${maxRating} stars`}
      >
        {Array.from({ length: maxRating }, (_, index) => renderStar(index))}
      </div>
      
      {showCount && count !== undefined && (
        <span className="text-sm text-warm-gray ml-1">
          ({count.toLocaleString()})
        </span>
      )}
    </div>
  );
};

export default StarRating;

